import React, { useState, useRef, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building, ArrowRight, Shield, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import { startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser';
import { tokenStorage } from '../utils/tokenStorage';
import { getTenantFromUrl } from '../utils/enhancedTenantUtils';

interface MfaMethod {
  id: number;
  type: 'totp' | 'email' | 'passkey' | 'sms' | 'voice';
  label: string;
  isDefault: boolean;
  requiresSend: boolean;
}

// Where to send the user after a successful login. Normally the app home, but
// if they were bounced here mid-flow (e.g. the OAuth consent page stored a
// return path), honor that once. Only same-origin app paths are allowed.
function postLoginDestination(fallback: string): string {
  try {
    const saved = sessionStorage.getItem('post_login_redirect');
    if (saved) {
      sessionStorage.removeItem('post_login_redirect');
      if (saved.startsWith('/') && !saved.startsWith('//')) return saved;
    }
  } catch { /* sessionStorage unavailable — use fallback */ }
  return fallback;
}

const MFA_METHOD_LABELS: Record<string, string> = {
  totp: 'Authenticator app',
  email: 'Email',
  passkey: 'Passkey',
  sms: 'Text message',
  voice: 'Phone call',
};

const Login: React.FC = () => {
  const { login, user } = useAuth();
  const [videoEnded, setVideoEnded] = useState(false);
  const navigate = useNavigate();
  const tenantInfo = getTenantFromUrl();
  // Match backend passwordResetPolicy: Stemmler/default surface only.
  const allowSelfServiceReset = tenantInfo.isMainDomain || tenantInfo.isDefault;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [availableMethods, setAvailableMethods] = useState<MfaMethod[]>([]);
  const [activeMethod, setActiveMethod] = useState<MfaMethod | null>(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoAvailable, setSsoAvailable] = useState(false);
  // Bumped when the user abandons an in-flight passkey ceremony (or starts a new
  // one) so a late WebAuthn resolve/reject cannot still sign them in or toast.
  const passkeyAttemptRef = useRef(0);

  React.useEffect(() => {
    // Check if user was blocked by IP whitelist
    const ipBlockedMessage = sessionStorage.getItem('ip_blocked_message');
    if (ipBlockedMessage) {
      toast.error(ipBlockedMessage, { duration: 6000 });
      sessionStorage.removeItem('ip_blocked_message');
    }

    // Check if SSO is available
    const checkSsoAvailability = async () => {
      try {
        const response = await api.get('/api/sso/config');
        setSsoAvailable(response.data.enabled || false);
      } catch (error) {
        setSsoAvailable(false);
      }
    };

    checkSsoAvailability();
  }, []);


  if (user) {
    return <Navigate to={postLoginDestination('/app/smart-start')} replace />;
  }

  const sendCodeFor = async (method: MfaMethod, token: string) => {
    try {
      setSendingCode(true);
      const response = await api.post('/api/mfa/challenge/send', {
        challengeToken: token,
        method: method.type,
        factorId: method.id,
      });
      toast.success(`Code sent to ${response.data.sentTo || 'your ' + MFA_METHOD_LABELS[method.type]}`);
    } catch (error: any) {
      const retryAfter = error.response?.data?.retryAfter;
      toast.error(
        retryAfter
          ? `A code was just sent — try again in ${retryAfter}s`
          : error.response?.data?.error || 'Could not send the code'
      );
    } finally {
      setSendingCode(false);
    }
  };

  const cancelPendingPasskey = () => {
    passkeyAttemptRef.current += 1;
    WebAuthnAbortService.cancelCeremony();
    setLoading(false);
  };

  const verifyWithPasskey = async (token: string) => {
    const attemptId = ++passkeyAttemptRef.current;
    try {
      setLoading(true);
      const optionsResponse = await api.post('/api/mfa/challenge/passkey/options', { challengeToken: token });
      if (attemptId !== passkeyAttemptRef.current) return;
      const assertion = await startAuthentication({ optionsJSON: optionsResponse.data.options });
      // Abandoned before we POST verify — challenge is still usable for TOTP/etc.
      if (attemptId !== passkeyAttemptRef.current) return;
      const verifyResponse = await api.post('/api/mfa/challenge/passkey/verify', {
        challengeToken: token,
        response: assertion,
      });

      // Once verify is in flight the server may consume the challenge. Always
      // finish sign-in on success even if the user clicked another method.
      if (verifyResponse.data.success) {
        const { accessToken, refreshToken } = verifyResponse.data;
        if (accessToken && refreshToken) {
          tokenStorage.setTokens(accessToken, refreshToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }
        toast.success('Signed in');
        window.location.href = postLoginDestination('/app/smart-start');
        return;
      }
      if (attemptId !== passkeyAttemptRef.current) return;
    } catch (error: any) {
      if (attemptId !== passkeyAttemptRef.current) return;
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
        toast('Passkey cancelled — pick another method or try again');
        return;
      }
      toast.error(error.response?.data?.error || 'Could not verify that passkey');
    } finally {
      if (attemptId === passkeyAttemptRef.current) {
        setLoading(false);
      }
    }
  };

  const switchMethod = async (method: MfaMethod) => {
    // Leaving passkey: drop the OS/browser ceremony so Authenticator (etc.) is usable
    // even while "Waiting for your passkey…" was still showing.
    if (activeMethod?.type === 'passkey' && method.type !== 'passkey') {
      cancelPendingPasskey();
    }
    setActiveMethod(method);
    setFormData((prev) => ({ ...prev, mfaCode: '' }));
    if (method.type === 'passkey' && challengeToken) {
      await verifyWithPasskey(challengeToken);
      return;
    }
    if (method.requiresSend && challengeToken) {
      await sendCodeFor(method, challengeToken);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (requiresMfa && pendingUserId) {
        // A passkey is confirmed by the browser ceremony, not by a typed code —
        // submitting the form must not post an empty code to /verify.
        if (activeMethod?.type === 'passkey') {
          if (challengeToken) await verifyWithPasskey(challengeToken);
          setLoading(false);
          return;
        }

        // Handle MFA verification using stored userId
        const mfaResponse = await api.post('/api/mfa/verify', {
          // Prefer the challenge token: it proves the password step happened and
          // is single-use. userId stays for a backend that predates it.
          ...(challengeToken ? { challengeToken } : { userId: pendingUserId }),
          method: activeMethod?.type,
          factorId: activeMethod?.id,
          token: formData.mfaCode
        });

        if (mfaResponse.data.success) {
          // Store tokens for backward compatibility (same as regular login)
          const { accessToken, refreshToken } = mfaResponse.data;
          if (accessToken && refreshToken) {
            tokenStorage.setTokens(accessToken, refreshToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          }

          toast.success('Login successful');
          window.location.href = postLoginDestination('/app');
        }
      } else {
        // Regular login - First check for password change or MFA requirements
        try {
          const loginResponse = await api.post('/api/auth/login', {
            email: formData.email,
            password: formData.password
          });

          // Check if password change is required (admin-created accounts)
          if (loginResponse.data.requiresPasswordChange) {
            toast('Password change required', {
              icon: '🔒',
              duration: 4000,
            });
            navigate('/forced-password-change', {
              state: { tempToken: loginResponse.data.tempToken }
            });
            setLoading(false);
            return;
          }

          // Check if MFA SETUP is required (privileged accounts without MFA configured)
          if (loginResponse.data.requiresMfaSetup) {
            toast('MFA setup required for administrator accounts', {
              icon: '🔐',
              duration: 5000,
            });
            navigate('/mfa-setup', {
              state: {
                mfaSetupToken: loginResponse.data.mfaSetupToken,
                userId: loginResponse.data.userId,
                userEmail: loginResponse.data.userEmail,
                enforced: true,
                mfaInstruction: loginResponse.data.mfaInstruction || null
              }
            });
            setLoading(false);
            return;
          }

          // Check if MFA verification is required (user has MFA already enabled)
          if (loginResponse.data.requiresMfa && loginResponse.data.userId) {
            const methods: MfaMethod[] = loginResponse.data.availableMethods || [];
            const preferred = methods.find((m) => m.isDefault) || methods[0] || null;

            setPendingUserId(loginResponse.data.userId);
            setChallengeToken(loginResponse.data.challengeToken || null);
            setAvailableMethods(methods);
            setActiveMethod(preferred);
            setRequiresMfa(true);

            if (preferred?.type === 'passkey' && loginResponse.data.challengeToken) {
              setLoading(false);
              await verifyWithPasskey(loginResponse.data.challengeToken);
              return;
            } else if (preferred?.requiresSend && loginResponse.data.challengeToken) {
              await sendCodeFor(preferred, loginResponse.data.challengeToken);
            } else {
              toast.success('Enter your 6-digit MFA code');
            }
            setLoading(false);
            return;
          }

          // Normal login flow - use AuthContext
          const result = await login(formData.email, formData.password);
          if (!result) {
            setLoading(false);
          }
          // If result === true, the login was successful and the page will redirect
        } catch (error: any) {
          console.error('Login check error:', error);
          toast.error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
          setLoading(false);
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
      setLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    try {
      // First check if SSO is configured by making a request to the config endpoint
      const configResponse = await api.get('/api/sso/config');
      
      if (!configResponse.data.enabled) {
        toast.error('SSO is not configured on this server');
        setSsoLoading(false);
        return;
      }
      
      // Redirect to backend SSO login endpoint
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      window.location.href = `${baseUrl}/api/sso/login`;
    } catch (error: any) {
      console.error('SSO login error:', error);
      if (error.response?.status === 400) {
        toast.error('SSO is not properly configured');
      } else {
        toast.error('SSO login failed');
      }
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0" style={{ background: '#07112E' }}></div>

      {/* Content */}
      <div className="max-w-md w-full space-y-8 relative z-20">
        <div className="text-center">
          <div className="mx-auto mb-6 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <video
              src="/jarvis-intro.mp4"
              autoPlay
              muted
              playsInline
              onEnded={() => setVideoEnded(true)}
              className="w-full"
              style={{ display: videoEnded ? 'none' : 'block', marginTop: '-8%', marginBottom: '-8%' }}
            />
            <img
              src="/Gemini_Generated_Image_t8j0d2t8j0d2t8j0.png"
              alt="Jarvis Logo"
              className="w-full object-contain"
              style={{ display: videoEnded ? 'block' : 'none' }}
            />
          </div>
        </div>

        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-2xl p-8 border border-white border-opacity-20">
          {/* SSO Login Option - Only show if available */}
          {ssoAvailable && (
            <>
              <div className="mb-6">
                <button
                  onClick={handleSsoLogin}
                  disabled={ssoLoading}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {ssoLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <Building className="w-5 h-5 mr-3 text-gray-500" />
                      <span>Sign in with SSO</span>
                      <ArrowRight className="w-4 h-4 ml-2 text-gray-400" />
                    </>
                  )}
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-electric-blue focus:border-electric-blue transition-colors"
                placeholder="Enter your email address"
                required
                disabled={requiresMfa}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-electric-blue focus:border-electric-blue transition-colors"
                  placeholder="Enter your password"
                  required
                  disabled={requiresMfa}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  disabled={requiresMfa}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {requiresMfa && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Smartphone className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Two-Factor Authentication Required</span>
                </div>
                {activeMethod?.type === 'passkey' ? (
                  <div>
                    <p className="text-sm text-gray-700 mb-3">
                      Confirm with your passkey — Face ID, Touch ID, or your security key.
                    </p>
                    <button
                      type="button"
                      onClick={() => challengeToken && verifyWithPasskey(challengeToken)}
                      disabled={loading || !challengeToken}
                      className="w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-electric-blue hover:bg-darker-blue disabled:opacity-50"
                    >
                      {loading ? 'Waiting for your passkey…' : 'Use passkey'}
                    </button>

                    {availableMethods.length > 1 && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs text-gray-600 mb-2">Or verify another way:</p>
                        <div className="flex flex-wrap gap-2">
                          {availableMethods
                            .filter((m) => m.id !== activeMethod?.id)
                            .map((method) => (
                              <button
                                key={method.id}
                                type="button"
                                onClick={() => switchMethod(method)}
                                disabled={sendingCode}
                                className="px-3 py-1 text-xs border border-blue-300 text-blue-700 rounded-full hover:bg-blue-100 disabled:opacity-50"
                              >
                                {MFA_METHOD_LABELS[method.type] || method.type}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                <div>
                  <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-2">
                    6-Digit Authentication Code
                  </label>
                  <input
                    id="mfaCode"
                    type="text"
                    value={formData.mfaCode}
                    onChange={(e) => setFormData({ ...formData, mfaCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    {activeMethod?.requiresSend
                      ? `Enter the 6-digit code we sent to your ${MFA_METHOD_LABELS[activeMethod.type]?.toLowerCase() || 'device'}`
                      : 'Enter the 6-digit code from your authenticator app'}
                  </p>

                  {activeMethod?.requiresSend && challengeToken && (
                    <button
                      type="button"
                      onClick={() => sendCodeFor(activeMethod, challengeToken)}
                      disabled={sendingCode}
                      className="text-xs text-blue-700 hover:text-blue-900 underline mt-2 disabled:opacity-50"
                    >
                      {sendingCode ? 'Sending…' : 'Resend code'}
                    </button>
                  )}

                  {availableMethods.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-xs text-gray-600 mb-2">Or verify another way:</p>
                      <div className="flex flex-wrap gap-2">
                        {availableMethods
                          .filter((m) => m.id !== activeMethod?.id)
                          .map((method) => (
                            <button
                              key={method.id}
                              type="button"
                              onClick={() => switchMethod(method)}
                              disabled={sendingCode}
                              className="px-3 py-1 text-xs border border-blue-300 text-blue-700 rounded-full hover:bg-blue-100 disabled:opacity-50"
                            >
                              {MFA_METHOD_LABELS[method.type] || method.type}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (requiresMfa && activeMethod?.type !== 'passkey' && formData.mfaCode.length !== 6)}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-electric-blue hover:bg-darker-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-electric-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {requiresMfa ? 'Verifying...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  {requiresMfa ? 'Verify & Sign In' : 'Sign In'}
                </>
              )}
            </button>

            {!requiresMfa && (
              <div className="mt-3 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-dusky-purple hover:text-darker-blue transition-colors"
                >
                  {allowSelfServiceReset
                    ? 'Forgot your password?'
                    : 'Need a password reset? Contact your admin'}
                </Link>
              </div>
            )}
          </form>

          {requiresMfa && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setRequiresMfa(false);
                  setFormData({ ...formData, mfaCode: '' });
                }}
                className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
              >
                ← Back to login
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-dusky-purple hover:text-darker-blue transition-colors">
                Sign up
              </Link>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Need a tenant for your organization?{' '}
              <Link to="/signup" className="font-medium text-dusky-purple hover:text-darker-blue transition-colors">
                Request Tenant
              </Link>
            </p>
          </div>

          {/* Security Features Notice */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-sm text-gray-600">
              <Shield className="w-4 h-4 mr-2 text-gray-500" />
              <span>Secure login with MFA and SSO support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;